"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.multiLanguageStringSchema = void 0;
exports.resolveMultiLanguageString = resolveMultiLanguageString;
exports.originalTextOf = originalTextOf;
exports.hasTranslationFor = hasTranslationFor;
exports.displayTextOf = displayTextOf;
exports.translationsMapOf = translationsMapOf;
/**
 * Shared wire type for a field that may carry machine translations, and the
 * pure resolver that builds one from a stored per-locale translations map.
 *
 * A plain string means the field has no translations to offer — the string
 * already is the original. The object form carries the original alongside a
 * `translations` map keyed by locale, so a reader resolves its own locale out
 * of it and falls back to `originalText`. Keeping the locale key means the
 * payload says which language each string is in, rather than relying on the
 * request it was paired with; that is what lets one type serve both a
 * viewer-narrowed read (one entry) and an every-locale read (`'*'`, every
 * entry). Because the type is self-contained it also nests, so a translatable
 * string inside an array (a poll option) needs no second map beside it.
 *
 * `originalLanguage` (the source language, when known) is not part of this
 * type — it travels as a sibling field on the containing wire type
 * (`Comment`, `CommentTeaserPreview`), since it's known independent of
 * whether any translation exists.
 */
const z = __importStar(require("zod"));
const locale_1 = require("../locale");
exports.multiLanguageStringSchema = z.union([
    z.string().describe('The text, with no translations available — the string is the original.'),
    z
        .object({
        originalText: z.string().describe('The untranslated original.'),
        translations: z
            .partialRecord(z.enum(locale_1.ALL_ISO_LOCALES), z.string())
            .describe('Translations of the text, keyed by locale. Carries the requested locale, or every locale for a `*` read.'),
    })
        .describe('The text, alongside the translations of it the reader may choose from.'),
]);
/**
 * Resolves a stored field + its per-locale translations map into a
 * `MultiLanguageString` for one viewer locale, or for every locale when
 * `locale` is `'*'`. Called only at the wire-mapper boundary — internal server
 * types keep carrying the raw per-locale map through the pipeline unchanged.
 *
 * Collapses to the plain-string form when no locale survives the selection, so
 * "nothing to offer this reader" and "never translated" ship identically.
 *
 * Empty text has no translations, whatever the stored map still says. The map
 * is written by an async queue job and never pruned, so a field blanked in
 * place — a soft-deleted comment, whose text is set to `''` — keeps the
 * translations of what it used to say, and the queue skips re-translating it
 * because the new value is empty. Reading the emptiness off the text keeps
 * that stale content from shipping whenever it was blanked, rather than
 * relying on the stored map being cleaned up.
 */
function resolveMultiLanguageString({ text, translations, field, locale, }) {
    if (!text)
        return text;
    const selected = {};
    for (const isoLocale of locale_1.ALL_ISO_LOCALES) {
        if (locale !== '*' && locale !== isoLocale)
            continue;
        const translated = translations?.[isoLocale]?.[field];
        if (translated)
            selected[isoLocale] = translated;
    }
    return Object.keys(selected).length > 0 ? { originalText: text, translations: selected } : text;
}
/** The original (untranslated) text. */
function originalTextOf(value) {
    return typeof value === 'string' ? value : value.originalText;
}
/**
 * Whether the value carries a translation this viewer would actually see.
 * A value can carry other locales — an every-locale read sends them all — so
 * carrying translations at all is a different question.
 */
function hasTranslationFor(value, viewerLocale) {
    return typeof value !== 'string' && !!value.translations[viewerLocale];
}
/**
 * The text to display to a viewer: their locale's translation if the value
 * carries one, else the original.
 *
 * An empty translation counts as no translation. Nothing in the translation
 * pipeline guards against a provider returning an empty string, and rendering
 * that blank would hide text the viewer could otherwise have read in the
 * original.
 */
function displayTextOf(value, viewerLocale) {
    if (typeof value === 'string')
        return value;
    return value.translations[viewerLocale] || value.originalText;
}
/**
 * The `locale -> field -> text` map of the stored comment shape, rebuilt from
 * the locale map a `MultiLanguageString` carries. `undefined` when it carries
 * none, which is how the stored shape spells "no translation".
 */
function translationsMapOf(value, field) {
    if (typeof value === 'string')
        return undefined;
    const map = {};
    for (const [locale, translated] of Object.entries(value.translations)) {
        map[locale] = { [field]: translated };
    }
    return Object.keys(map).length > 0 ? map : undefined;
}
//# sourceMappingURL=multi-language.js.map